/**
 * Centralized artifact type constants and capability registry
 *
 * packages/shared/src/schema/artifact-types/ is the single source of truth for
 * artifact type data. `pnpm artifact-types:generate` produces
 * `artifact-types.generated.ts` which this file re-exports, keeping
 * all existing import paths working unchanged.
 *
 * What stays hand-written here (structural code that does not vary per type):
 *   - ArtifactTypeConfig interface (the shape of a registry entry)
 *   - AttachmentExtraction, ArtifactParentSubtype, ParentEntityType type aliases
 *   - All helper functions (getArtifactTypeConfig, isValidArtifactType, etc.)
 *
 * What is generated (packages/shared/src/schema/artifact-types/ → re-exported below):
 *   - ARTIFACT_TYPES tuple, ArtifactType union
 *   - ARTIFACT_TYPE legacy enum-style constant (callers use ARTIFACT_TYPE.NOTE etc.)
 *   - ARTIFACT_TYPE_CONFIG record
 *   - SYSTEM_INTERNAL_ARTIFACT_TYPES, USER_FACING_ARTIFACT_TYPES
 */
// Re-export generated data tables so all existing import paths keep working.
export { ARTIFACT_TYPES, ARTIFACT_TYPE, ARTIFACT_TYPE_CONFIG, SYSTEM_INTERNAL_ARTIFACT_TYPES, USER_FACING_ARTIFACT_TYPES, } from './artifact-types.generated';
// Import the generated values for use in this file's helper functions.
import { ARTIFACT_TYPES, ARTIFACT_TYPE_CONFIG, SYSTEM_INTERNAL_ARTIFACT_TYPES, } from './artifact-types.generated';
// --- Helper functions ---
export const getArtifactTypeConfig = (artifactType) => {
    return ARTIFACT_TYPE_CONFIG[artifactType];
};
export const getArtifactDisplayName = (artifactType) => {
    if (isValidArtifactType(artifactType)) {
        return ARTIFACT_TYPE_CONFIG[artifactType].displayName;
    }
    return 'Item'; // fallback for unknown types
};
// Validation helper
export const isValidArtifactType = (type) => {
    return ARTIFACT_TYPES.includes(type);
};
/** True if this artifact type is for internal infrastructure only. */
export const isSystemInternalArtifactType = (type) => {
    if (!isValidArtifactType(type))
        return false;
    return SYSTEM_INTERNAL_ARTIFACT_TYPES.includes(type);
};
/**
 * The registry-true list of artifact types for a capability flag — the single
 * source every MCP tool enum derives from. Excludes system-internal types
 * (e.g. protocol_annotations) unless `includeInternal` is set.
 */
export const artifactTypesForCapability = (flag, opts = {}) => ARTIFACT_TYPES.filter((t) => ARTIFACT_TYPE_CONFIG[t][flag] &&
    (opts.includeInternal || !SYSTEM_INTERNAL_ARTIFACT_TYPES.includes(t)));
/**
 * Minimum content length (chars) required before AI title generation is attempted.
 * Shared between backend (eligibility check) and frontend (skip API call).
 */
export const MIN_TITLE_CONTENT_LENGTH = 50;
/**
 * Wire values the artifact list accepts as `sortBy`. SSOT for the REST query
 * param, the MCP `list_artifacts` input enum, and the frontend hook's option
 * type — all three previously hand-copied this list.
 *
 * The backend maps each value to a column in
 * `services/artifact/artifact.sort-fields.ts`; that map is declared
 * `satisfies Record<ArtifactSortBy, string>`, so adding a value here without
 * mapping it fails typecheck instead of failing silently at runtime (an
 * unmapped value parses to `undefined` and the service falls back to
 * `updated` — the header arrow moves, the rows do not).
 *
 * Widening this list is a keyset-pagination decision, not a preference: read
 * the docblock on `SORT_FIELD_MAP` before adding one.
 */
export const ARTIFACT_SORT_BY = ['title', 'created', 'updated', 'type'];
/**
 * The Protocols list's "Last run" sort: when each protocol last ran (its
 * runs' latest completion or activity), never-run protocols last in both
 * directions.
 */
export const LAST_RUN_SORT = 'lastRun';
/** The Protocols list's "Versions" sort: how many versions each protocol has. */
export const VERSIONS_SORT = 'versions';
/**
 * The sorts no artifact column holds. Not in `ARTIFACT_SORT_BY`: each is
 * computed per page (`services/artifact/artifact.computed-sort-page.ts`) and
 * accepted only on a protocols-only listing of `GET /api/artifacts`.
 */
export const PROTOCOL_COMPUTED_SORTS = [LAST_RUN_SORT, VERSIONS_SORT];
// --- Entity type predicates ---
/**
 * Whether the given type string represents any artifact entity type.
 * Matches both specific artifact types ('note', 'attachment', 'protocol')
 * and the generic 'artifact' hierarchy node type.
 * Use this instead of: type === 'artifact' || type === 'note' || type === 'attachment' || ...
 */
export const isArtifactEntityType = (type) => {
    return type === 'artifact' || isValidArtifactType(type);
};
// --- Capability predicates ---
// Use these instead of checking artifactType === 'note' directly.
/** Whether this artifact type uses TipTap rich text content. */
export const isRichTextArtifact = (type) => {
    if (!isValidArtifactType(type))
        return false;
    return ARTIFACT_TYPE_CONFIG[type].hasRichTextContent;
};
/** Whether this artifact type stores files in cloud storage. */
export const isFileArtifact = (type) => {
    if (!isValidArtifactType(type))
        return false;
    return ARTIFACT_TYPE_CONFIG[type].hasFileStorage;
};
/** Whether this artifact type should generate embeddings. */
export const artifactGeneratesEmbeddings = (type) => {
    if (!isValidArtifactType(type))
        return false;
    return ARTIFACT_TYPE_CONFIG[type].generatesEmbeddings;
};
/** Whether this artifact type has a full-page detail view. */
export const artifactHasDetailPage = (type) => {
    if (!isValidArtifactType(type))
        return false;
    return ARTIFACT_TYPE_CONFIG[type].hasDetailPage;
};
/**
 * Get the detail route for an artifact, with :id replaced.
 * Returns null if the artifact type doesn't have a detail page.
 */
export const getArtifactDetailRoute = (type, id) => {
    if (!isValidArtifactType(type))
        return null;
    const config = ARTIFACT_TYPE_CONFIG[type];
    if (!config.hasDetailPage || !config.detailRoutePath)
        return null;
    return config.detailRoutePath.replace(':id', id);
};
// Operation message helpers (for consistent user messaging)
export const getArtifactOperationMessage = (operation, artifactType, isSuccess = false) => {
    const displayName = getArtifactDisplayName(artifactType);
    if (isSuccess) {
        const successMessages = {
            create: `${displayName} Created!`,
            update: `${displayName} Updated!`,
            delete: `${displayName} Deleted!`,
            fetch: `${displayName} Loaded!`,
        };
        return successMessages[operation] || `${displayName} ${operation}d successfully!`;
    }
    const errorMessages = {
        create: `Failed to create ${displayName.toLowerCase()}`,
        update: `Failed to update ${displayName.toLowerCase()}`,
        delete: `Failed to delete ${displayName.toLowerCase()}`,
        fetch: `Failed to fetch ${displayName.toLowerCase()}`,
    };
    return errorMessages[operation] || `Failed to ${operation} ${displayName.toLowerCase()}`;
};
export const getArtifactOperationDescription = (operation, artifactType, title) => {
    const displayName = getArtifactDisplayName(artifactType);
    const itemName = title || displayName.toLowerCase();
    const descriptions = {
        create: `${itemName} has been added to the project`,
        update: `${itemName} has been updated`,
        delete: `${itemName} has been deleted`,
    };
    return descriptions[operation] || `${itemName} has been ${operation}d`;
};
//# sourceMappingURL=artifact.js.map